import { ModuleWithProviders, NgModule, APP_INITIALIZER } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { InactivityTimeDialogModule } from '../../shared/inactivity-time-dialog/inactivity-time-dialog.module';
import { StoreModule } from '@ngrx/store';
import { authReducer, featureName } from '../../store/auth.reducer';
import { OAuthModule, OAuthService } from 'angular-oauth2-oidc';
import { EffectsModule } from '@ngrx/effects';
import { KeycloakAuthEffects } from './store/keycloak-auth.effects';
import { IframeAuthEffects } from '../../store/iframe-auth.effects';
import { MyKeycloakAuthRoutingModule } from './my-keycloak-auth-routing.module';
import { SdCacheModule } from 'src/app/shared/sd-cache/sd-cache.module';
import { KeycloakDynamicConfigService } from './services/keycloak-dynamic-config.service';
import { environment } from '../../../../environments/environment';
import { ClientNames } from 'src/app/clients/client-names.enum';
import { Es01ClientGlobalStore } from 'src/app/clients/es01/store/es01-client-global.store';

/**
 * Factory function to initialize Keycloak configuration
 * This will be called during app initialization
 */
export function initializeKeycloakDynamicConfig(
  dynamicConfigService: KeycloakDynamicConfigService,
  oauthService: OAuthService,
  es01ClientStore: InstanceType<typeof Es01ClientGlobalStore>
): () => Promise<void> {
  return async (): Promise<void> => {
    console.log('Initializing dynamic Keycloak configuration...');

    // For es-01, the collection must be loaded before keycloak config so the
    // brand code can be resolved by full-host match. A failed fetch is swallowed
    // by the store (falls back to env/subdomain defaults) and never blocks boot.
    if (environment.appName === ClientNames.es01) {
      await es01ClientStore.loadCollection();
    }

    return firstValueFrom(dynamicConfigService.fetchKeycloakConfig())
      .then(dynamicConfig => {
        // Configuration validation is now done in the service
        const authConfig = dynamicConfigService.createAuthConfig(dynamicConfig);
        oauthService.configure(authConfig);
        console.log('✅ Dynamic Keycloak configuration applied:', authConfig);
      })
      .catch(error => {
        console.error(
          '❌ Failed to initialize dynamic Keycloak configuration:',
          error
        );

        // Store error state for the app to check after bootstrap
        sessionStorage.setItem(
          'keycloak-config-error',
          JSON.stringify({
            error: error.message || 'Configuration failed',
            timestamp: new Date().toISOString(),
            details: error,
          })
        );

        // Always resolve to allow app to bootstrap
        return Promise.resolve();
      });
  };
}

export function initializeKeycloakConditionalConfig(
  dynamicConfigService: KeycloakDynamicConfigService,
  oauthService: OAuthService,
  es01ClientStore: InstanceType<typeof Es01ClientGlobalStore>
): () => Promise<void> {
  if (window.self !== window.top) {
    // Inside an iframe keycloak init is skipped (IframeAuthEffects drives auth
    // instead). But es-01 still runs the start flow / resolvers, which resolve the
    // brand by full-host match from the client collection — so it must be loaded
    // here too, or host-based resolution silently falls back to the subdomain.
    return async (): Promise<void> => {
      if (environment.appName === ClientNames.es01) {
        await es01ClientStore.loadCollection();
      }
    };
  }
  return initializeKeycloakDynamicConfig(
    dynamicConfigService,
    oauthService,
    es01ClientStore
  );
}

@NgModule({
  imports: [
    OAuthModule.forRoot(),
    StoreModule.forFeature(featureName, authReducer),
    EffectsModule.forFeature(
      window.self !== window.top ? [IframeAuthEffects] : [KeycloakAuthEffects]
    ),
    SdCacheModule,
    InactivityTimeDialogModule,
    MyKeycloakAuthRoutingModule,
  ],
})
export class KeycloakDynamicAuthModule {
  static forRoot(): ModuleWithProviders<KeycloakDynamicAuthModule> {
    return {
      ngModule: KeycloakDynamicAuthModule,
      providers: [
        KeycloakDynamicConfigService,
        {
          provide: APP_INITIALIZER,
          useFactory: initializeKeycloakConditionalConfig,
          deps: [
            KeycloakDynamicConfigService,
            OAuthService,
            Es01ClientGlobalStore,
          ],
          multi: true,
        },
      ],
    };
  }
}
